import { useState } from 'react';
import { Search } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  useCommsProfileQuery,
  useDeliveryHistoryQuery,
  useUserPreferencesQuery,
} from '@/client/api/campaigns/campaigns.query';
import { useSetUserPreferenceMutation } from '@/client/api/campaigns/campaigns.mutation';
import { formatEpoch } from '@/client/types/campaigns';
import { toast } from 'sonner';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';

const TriState = ({
  value,
  onChange,
  disabled,
}: {
  value: boolean | null | undefined;
  onChange: (v: boolean | null) => void;
  disabled?: boolean;
}) => (
  <select
    disabled={disabled}
    value={value === true ? '1' : value === false ? '0' : ''}
    onChange={(e) => {
      const v = e.target.value;
      onChange(v === '1' ? true : v === '0' ? false : null);
    }}
    className="rounded border border-gray-300 bg-white px-2 py-1 text-xs"
  >
    <option value="">Inherit</option>
    <option value="1">On</option>
    <option value="0">Off</option>
  </select>
);

export default function CampaignUserCommsPanel({ canManage }: { canManage: boolean }) {
  const [uidInput, setUidInput] = useState('');
  const [uid, setUid] = useState<number | null>(null);

  const profileQ = useCommsProfileQuery(uid, uid != null);
  const prefsQ = useUserPreferencesQuery(uid, uid != null);
  const historyQ = useDeliveryHistoryQuery(uid, { limit: 25, offset: 0 }, uid != null);
  const setPref = useSetUserPreferenceMutation();

  const profile = profileQ.data?.comms_profile;
  const preferences = prefsQ.data?.preferences ?? [];
  const history = historyQ.data?.history ?? historyQ.data?.deliveries ?? [];

  const lookup = () => {
    const n = Number(uidInput.trim());
    if (!Number.isFinite(n) || n <= 0) {
      toast.error('Enter a valid UID', { richColors: true });
      return;
    }
    setUid(n);
  };

  const savePref = async (
    ntypid: number,
    patch: {
      channel_email?: boolean | null;
      channel_sms?: boolean | null;
      channel_in_app?: boolean | null;
    },
  ) => {
    if (!uid || !canManage) return;
    const current = preferences.find((p) => p.ntypid === ntypid);
    try {
      await setPref.mutateAsync({
        uid,
        body: {
          ntypid,
          channel_email: patch.channel_email !== undefined ? patch.channel_email : current?.channel_email ?? null,
          channel_sms: patch.channel_sms !== undefined ? patch.channel_sms : current?.channel_sms ?? null,
          channel_in_app:
            patch.channel_in_app !== undefined ? patch.channel_in_app : current?.channel_in_app ?? null,
        },
        idempotencyKey: generateIdempotencyKey(),
      });
      toast.success('Preference saved', { richColors: true });
    } catch (error: any) {
      toast.error(error?.message || 'Save failed', { richColors: true });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm sm:flex-row sm:items-end">
        <div className="flex-1">
          <label className="mb-1 block text-xs text-gray-500">User UID</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="number"
              value={uidInput}
              onChange={(e) => setUidInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && lookup()}
              placeholder="102"
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <Button type="button" onClick={lookup}>
          Look up
        </Button>
      </div>

      {uid == null && (
        <p className="rounded-xl border border-dashed border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          Enter a UID to load comms profile, preferences, and delivery history.
        </p>
      )}

      {uid != null && (profileQ.isLoading || prefsQ.isLoading) && (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      )}

      {uid != null && profileQ.isError && (
        <p className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
          Could not load comms profile for UID {uid}.
        </p>
      )}

      {profile && (
        <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-gray-900">
            {profile.name || `User ${profile.uid}`}
          </h3>
          <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt className="text-xs text-gray-500">Email</dt>
              <dd>
                {profile.email1 || '—'}
                {profile.email1_verified ? ' · verified' : ''}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Phone</dt>
              <dd>
                {profile.phone1 || '—'}
                {profile.phone1_verified ? ' · verified' : ''}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Locale / TZ</dt>
              <dd>
                {profile.locale || '—'} · {profile.timezone || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">SMS suppressed</dt>
              <dd>{profile.sms_suppressed ? 'Yes' : 'No'}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">WhatsApp opted out</dt>
              <dd>{profile.whatsapp_opted_out ? 'Yes' : 'No'}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Flags</dt>
              <dd>
                {[
                  profile.admin_hold ? 'admin hold' : null,
                  profile.tester ? 'tester' : null,
                ]
                  .filter(Boolean)
                  .join(', ') || '—'}
              </dd>
            </div>
          </dl>
        </div>
      )}

      {uid != null && !prefsQ.isLoading && preferences.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Notification preferences</h3>
            <p className="text-xs text-gray-500">
              null = inherit type default. Editing requires messaging.manage.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">In-app</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">SMS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {preferences.map((p) => (
                  <tr key={p.ntypid}>
                    <td className="px-4 py-3">
                      <p className="font-medium">{p.name || p.name_system || p.ntypid}</p>
                      <p className="font-mono text-xs text-gray-500">#{p.ntypid}</p>
                    </td>
                    <td className="px-4 py-3">
                      <TriState
                        value={p.channel_in_app}
                        disabled={!canManage || setPref.isPending}
                        onChange={(v) => savePref(p.ntypid, { channel_in_app: v })}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <TriState
                        value={p.channel_email}
                        disabled={!canManage || setPref.isPending}
                        onChange={(v) => savePref(p.ntypid, { channel_email: v })}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <TriState
                        value={p.channel_sms}
                        disabled={!canManage || setPref.isPending}
                        onChange={(v) => savePref(p.ntypid, { channel_sms: v })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {uid != null && (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Delivery history</h3>
          </div>
          {historyQ.isLoading && (
            <div className="space-y-2 p-4">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          )}
          {!historyQ.isLoading && history.length === 0 && (
            <p className="p-6 text-center text-sm text-gray-500">No delivery history.</p>
          )}
          {history.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">When</th>
                    <th className="px-4 py-3 font-medium">Channel</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Subject</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.map((h, i) => (
                    <tr key={h.msgid || i}>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                        {formatEpoch(h.created_at)}
                      </td>
                      <td className="px-4 py-3">{h.channel || '—'}</td>
                      <td className="px-4 py-3">{h.status || '—'}</td>
                      <td className="max-w-[240px] truncate px-4 py-3">{h.subject || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
